import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import AdPlaceholder from "@/components/AdPlaceholder";
import PopularTools from "@/components/PopularTools";
import { byCategory, CALCULATORS } from "@/data/calculators";

export default function Home() {
    const cats = byCategory();
    const featured = CALCULATORS.filter((c) => c.featured);

    const heroImg =
        "https://images.unsplash.com/photo-1724627561948-3004cc467dc6?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";

    return (
        <>
            <Seo
                title="FinCalc — Free finance calculators. Straightforward money math."
                description="Editorial-grade calculators for mortgages, loans, retirement, debt payoff, savings, salary, and taxes. Fast, accurate, free."
                canonical="/"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        name: "FinCalc",
                        legalName: "Hi Tex Lawn Pro LLC",
                        url: "https://afincalc.com",
                        description: "FinCalc — financial calculators by Hi Tex Lawn Pro LLC.",
                        parentOrganization: {
                            "@type": "Organization",
                            name: "Hi Tex Lawn Pro LLC",
                        },
                        // Bidirectional entity-cluster anchor with sister site
                        // (TurfPulse declares afincalc.com via sameAs on its side).
                        sameAs: ["https://getturfpulse.com"],
                        address: {
                            "@type": "PostalAddress",
                            addressLocality: "Round Rock",
                            addressRegion: "TX",
                            addressCountry: "US",
                        },
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        name: "FinCalc",
                        url: "https://afincalc.com",
                        publisher: {
                            "@type": "Organization",
                            name: "Hi Tex Lawn Pro LLC",
                        },
                        potentialAction: {
                            "@type": "SearchAction",
                            target: "https://afincalc.com/?q={search_term_string}",
                            "query-input": "required name=search_term_string",
                        },
                    },
                ]}
            />

            {/* HERO */}
            <section className="relative overflow-hidden border-b border-border">
                <div
                    className="absolute inset-0 opacity-[0.12] mix-blend-multiply"
                    style={{
                        backgroundImage: `url(${heroImg})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                    aria-hidden
                />
                {/* Left-fade overlay so hero text stays legible over the photo */}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(90deg, hsl(42 33% 96%) 0%, hsl(42 33% 96% / 0.95) 45%, hsl(42 33% 96% / 0.6) 75%, transparent 100%)",
                    }}
                    aria-hidden
                />
                <div className="absolute inset-0 grid-paper opacity-30" aria-hidden />
                <div className="container-editorial relative py-16 md:py-24 lg:py-32">
                    <div className="max-w-4xl anim-fade-up">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/5 border border-primary/10">
                            <Sparkles className="w-3.5 h-3.5 text-accent" strokeWidth={1.5} />
                            <span className="text-[11px] uppercase tracking-[0.2em] text-primary/70">
                                Free · No signup · Actually accurate
                            </span>
                        </div>
                        <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight text-primary text-balance">
                            Straightforward<br />
                            <span className="italic text-accent">money math.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-lg sm:text-xl leading-relaxed text-foreground/75 anim-fade-up-delay">
                            Editorial-grade calculators for the biggest money decisions you&apos;ll
                            ever make — mortgages, retirement, debt payoff, and the daily math in
                            between.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-3 anim-fade-up-delay-2">
                            {featured.map((f) => (
                                <Link
                                    key={f.slug}
                                    to={`/calculator/${f.slug}`}
                                    data-testid={`hero-featured-${f.slug}`}
                                    className="group inline-flex items-center gap-2 px-5 h-12 rounded-md bg-primary text-primary-foreground hover:bg-secondary transition-colors font-medium text-sm"
                                >
                                    {f.short}
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.75} />
                                </Link>
                            ))}
                            <button
                                type="button"
                                onClick={() => {
                                    const el = document.getElementById("loans");
                                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                                className="inline-flex items-center gap-2 px-5 h-12 rounded-md border border-primary/20 text-primary hover:border-primary/40 transition-colors text-sm"
                                data-testid="hero-browse-all"
                            >
                                Browse all →
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats strip */}
            <section className="border-b border-border bg-card/40">
                <div className="container-editorial py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[
                        [String(CALCULATORS.length), "Calculators"],
                        ["$0", "Ever charged"],
                        ["100%", "Client-side math"],
                        ["0", "Data points sold"],
                    ].map(([num, label]) => (
                        <div key={label}>
                            <div className="font-serif text-4xl lg:text-5xl text-primary tabular">{num}</div>
                            <div className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                {label}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Leaderboard */}
            <div className="container-editorial mt-10">
                <AdPlaceholder type="leaderboard" dataTestId="home-ad-leaderboard" />
            </div>

            {/* Trending — live from /api/tools/popular */}
            <PopularTools />

            {/* Category sections */}
            {cats.map((cat) => (
                <section key={cat.id} id={cat.id} className="container-editorial py-16 scroll-mt-24">
                    <div className="flex items-end justify-between gap-6 mb-8 border-b border-border pb-4">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.2em] text-accent">
                                Category
                            </div>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-primary mt-1">
                                {cat.label}
                            </h2>
                        </div>
                        <p className="hidden md:block max-w-sm text-sm text-muted-foreground text-right">
                            {cat.blurb}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {cat.tools.map((t, i) => (
                            <Link
                                key={t.slug}
                                to={`/calculator/${t.slug}`}
                                data-testid={`home-card-${t.slug}`}
                                className="group bg-card border border-border rounded-xl p-6 hover:border-primary/30 hover:shadow-[0_8px_32px_rgba(10,35,24,0.08)] transition-all relative overflow-hidden"
                            >
                                <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ArrowRight className="w-5 h-5 text-accent" strokeWidth={1.5} />
                                </div>
                                <div className="font-mono tabular text-[11px] text-muted-foreground">
                                    {String(i + 1).padStart(2, "0")} / {cat.tools.length}
                                </div>
                                <h3 className="mt-4 font-serif text-2xl text-primary leading-snug">
                                    {t.title}
                                </h3>
                                <p className="mt-2 text-sm text-foreground/70 leading-relaxed line-clamp-3">
                                    {t.description}
                                </p>
                                <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-primary group-hover:text-accent transition-colors">
                                    Open calculator
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            ))}

            {/* Editorial pitch */}
            <section className="container-editorial py-20 border-t border-border">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-accent">
                            Our approach
                        </div>
                        <h2 className="font-serif text-4xl lg:text-5xl text-primary mt-3 leading-tight text-balance">
                            One number. Zero nonsense.
                        </h2>
                        <p className="mt-5 text-foreground/75 leading-relaxed text-[17px]">
                            Every calculator on FinCalc runs entirely in your browser. We don&apos;t
                            log your salary, your mortgage balance, or your retirement projection.
                            The math is open, the assumptions are labelled, and the results are
                            honest — including when the answer you were hoping for isn&apos;t the
                            answer the math gives you.
                        </p>
                    </div>
                    <div className="relative">
                        <div className="bg-primary text-primary-foreground rounded-2xl p-10 relative overflow-hidden">
                            <div className="absolute inset-0 noise opacity-40" aria-hidden />
                            <div className="relative">
                                <div className="font-mono tabular text-[11px] uppercase tracking-[0.2em] text-accent-bright">
                                    Example · 30-yr mortgage
                                </div>
                                <div className="mt-4 font-mono tabular text-5xl lg:text-6xl text-primary-foreground">
                                    $2,147
                                </div>
                                <div className="mt-1 text-sm opacity-70">
                                    /month on a $400K loan at 6.5%
                                </div>
                                <div className="mt-6 pt-6 border-t border-primary-foreground/20 grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest opacity-60">Total interest</div>
                                        <div className="font-mono tabular text-xl mt-1">$373K</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest opacity-60">Total paid</div>
                                        <div className="font-mono tabular text-xl mt-1">$773K</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
